/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { UserSession } from '../../session/UserSession';
import { AnalyticsReporter } from '../reporter/model/AnalyticsReporter';
import { User } from '../../model/User';
import { Logger } from '@quickplay/foundation';
import { UserEvent } from '../../event/UserEvent';
import {
  toApplicationErrorReportingData,
  toNPAWUserReportingData,
  toNewRelicUserReportingData,
  toUserErrorReportingData,
  toUserReportingData,
} from '../util/DataUtil';
import { UserError } from '../../model/UserError';
import { Provider } from '../Provider';
import { ApplicationEvent } from '../../event/ApplicationEvent';
import { ApplicationError } from '../../model/ApplicationError';

/** The application owns the Godavari ppid; it is reported only when the user carries one. */
const toGodavariUserReportingData = (user: User) => ({
  ...toUserReportingData(user),
  ...(user.ppid ? { app_ppid: user.ppid } : {}),
});

export class UserSessionImpl implements UserSession {
  constructor(
    private readonly reporters: { [key: string]: AnalyticsReporter },
    private user: User,
    private readonly loggers?: { [key: string]: Logger },
  ) {
    this.loggers &&
      Object.values(this.loggers).forEach(logger => {
        logger.log('[ANALYTICS] User session created');
      });
  }

  /** ---------------- User Data ---------------- */
  updateMetadata(user: User): void {
    this.user = user;
    this.reportMetadata(this.user);
  }

  /** ---------------- Session & Profile ---------------- */
  signup(user: User): void {
    this.setUserAndReport(user, UserEvent.SIGN_UP);
  }

  login(user: User): void {
    this.setUserAndReport(user, UserEvent.LOGIN);
  }

  logout(user?: User): void {
    this.user = user || this.user;
    this.reportEvent(UserEvent.LOGOUT);
  }

  forgotPassword(user: User): void {
    this.setUserAndReport(user, UserEvent.FORGOT_PASSWORD);
  }

  createProfile(user: User): void {
    this.setUserAndReport(user, UserEvent.CREATE_PROFILE);
  }

  updateProfile(user: User): void {
    this.setUserAndReport(user, UserEvent.UPDATE_PROFILE);
  }

  deleteProfile(user: User): void {
    this.user = user;
    this.reportEvent(UserEvent.DELETE_PROFILE);
  }

  /** ---------------- Subscription ---------------- */
  startSubscription(user: User): void {
    this.setUserAndReport(user, UserEvent.START_SUBSCRIPTION);
  }

  purchaseSubscription(user: User): void {
    this.setUserAndReport(user, UserEvent.PURCHASE_SUBSCRIPTION);
  }

  purchaseSubscriptionCancel(user: User): void {
    this.user = user;
    this.reportEvent(UserEvent.PURCHASE_SUBSCRIPTION_CANCEL);
  }

  purchaseSubscriptionFailure(user: User): void {
    this.user = user;
    this.reportEvent(UserEvent.PURCHASE_SUBSCRIPTION_FAILURE);
  }

  changeSubscription(user: User): void {
    this.setUserAndReport(user, UserEvent.CHANGE_SUBSCRIPTION);
  }

  completePayment(user: User): void {
    this.setUserAndReport(user, UserEvent.COMPLETE_PAYMENT);
  }

  /** ---------------- Errors ---------------- */
  addErrorEvent(error: UserError): void {
    const errorData = {
      ...error,
      errorType: error.errorType || 'USER_ERROR',
      severity: error.severity,
    };
    this.reportEvent(UserEvent.USER_ACTION_ERROR, errorData);
  }

  /** ---------------- Helpers ---------------- */
  private setUserAndReport(user: User, event: UserEvent): void {
    this.user = user;
    this.reportMetadata(this.user);
    this.reportEvent(event);
  }

  private reportMetadata(user: User): void {
    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      if (reporter.provider === Provider.NPAW) {
        reporter.reportMetadata(toNPAWUserReportingData(user));
      } else if (reporter.provider === Provider.GODAVARI) {
        reporter.reportMetadata(toGodavariUserReportingData(user));
      } else {
        reporter.reportMetadata(toUserReportingData(user));
      }
    });
  }

  private reportEvent(event: string, data?: Record<string, any>, context?: any): void {
    if (!this.reporters) return;

    Object.values(this.reporters).forEach(reporter => {
      const isNewRelic = reporter.provider === Provider.NEWRELIC;

      if (event === UserEvent.USER_ACTION_ERROR && data) {
        if (isNewRelic) {
          reporter.reportEvent(ApplicationEvent.ERROR, {
            ...toApplicationErrorReportingData(data as ApplicationError, reporter.provider),
            ...toNewRelicUserReportingData(this.user),
          });
        } else {
          reporter.reportEvent(event, toUserErrorReportingData(data), context);
        }
        return;
      }

      const payload = isNewRelic
        ? { ...data, ...toNewRelicUserReportingData(this.user) }
        : toUserReportingData(this.user);

      if (reporter.provider === Provider.GODAVARI) {
        reporter.reportMetadata(toGodavariUserReportingData(this.user));
        return;
      }
      reporter.reportEvent(event, payload, context);
    });
  }
}
